import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { Row } from 'reactstrap';
import PerfectScrollbar from 'react-perfect-scrollbar';
import EmojiPicker from 'emoji-picker-react';
import 'react-perfect-scrollbar/dist/css/styles.css';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';

import Loading from '~/components/Loading';
import MessageBubble from './components/MessageBubble';
import QuickReplyDropdown from './components/QuickReplyDropdown';
import TransferModal from './components/TransferModal';
import ConfirmModal from './components/ConfirmModal';

export default function ChatPanel({
  ticket,
  messages,
  loadingMessages,
  quickReplies,
  currentUser,
  onSendMessage,
  onResolveQuickReply,
  onTransfer,
  onClose,
  showDetails,
  onToggleDetails,
  onSendAttachment,
  onReopen,
}) {
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const emojiRef = useRef(null);

  const [currentMessage, setCurrentMessage] = useState('');
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [quickReplyFilter, setQuickReplyFilter] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [sendingAttachment, setSendingAttachment] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [ticket?.id]);

  useEffect(() => {
    if (!showEmojiPicker) return undefined;
    const handleClickOutside = e => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  const showDayRow = useCallback(
    (message, index) => {
      if (index === 0) return true;
      const curr = new Date(message.created_at);
      const prev = new Date(messages[index - 1].created_at);
      return (
        curr.getDate() !== prev.getDate() ||
        curr.getMonth() !== prev.getMonth() ||
        curr.getFullYear() !== prev.getFullYear()
      );
    },
    [messages]
  );

  const handleSend = useCallback(() => {
    const text = currentMessage.trim();
    if (!text || !ticket?.chat_id) return;
    onSendMessage(ticket.chat_id, text, ticket.subscriber_schema);
    setCurrentMessage('');
    setShowQuickReplies(false);
    if (inputRef.current) inputRef.current.focus();
  }, [
    currentMessage,
    ticket?.chat_id,
    ticket?.subscriber_schema,
    onSendMessage,
  ]);

  const handleFileSelect = useCallback(
    async e => {
      const file = e.target.files?.[0];
      if (!file || !ticket?.chat_id || !ticket?.subscriber_schema) return;

      if (file.size > 16 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Máximo 16MB.');
        return;
      }

      setSendingAttachment(true);
      try {
        await onSendAttachment(
          ticket.chat_id,
          file,
          null,
          ticket.subscriber_schema
        );
      } finally {
        setSendingAttachment(false);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [ticket?.chat_id, ticket?.subscriber_schema, onSendAttachment]
  );

  const handleInputChange = useCallback(e => {
    const val = e.target.value;
    setCurrentMessage(val);
    if (val.startsWith('/')) {
      setShowQuickReplies(true);
      setQuickReplyFilter(val);
    } else {
      setShowQuickReplies(false);
      setQuickReplyFilter('');
    }
  }, []);

  const handleSelectQuickReply = useCallback(
    async reply => {
      setShowQuickReplies(false);
      const resolved = await onResolveQuickReply(reply.shortcut, ticket?.id);
      if (resolved) setCurrentMessage(resolved);
      if (inputRef.current) inputRef.current.focus();
    },
    [onResolveQuickReply, ticket?.id]
  );

  const handleEmojiClick = useCallback(emojiData => {
    setCurrentMessage(prev => prev + emojiData.emoji);
    setShowEmojiPicker(false);
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const clientInitials = useMemo(() => {
    const name = ticket?.customer_name || '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    if (parts.length === 1 && parts[0]) return parts[0][0].toUpperCase();
    return '?';
  }, [ticket?.customer_name]);

  return (
    <div
      style={{
        border: '0.5px solid #dee2e6',
        borderRadius: '4px',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
      }}
    >
      {/* Header */}
      <div
        className="d-flex align-items-center justify-content-between"
        style={{
          padding: '10px 14px',
          borderBottom: '0.5px solid #dee2e6',
          flexShrink: 0,
        }}
      >
        <div className="d-flex align-items-center" style={{ gap: '10px' }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: '#E6F1FB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 500,
              fontSize: '13px',
              color: '#0C447C',
              flexShrink: 0,
            }}
          >
            {clientInitials}
          </div>
          <div>
            <div className="font-size-14 font-weight-600">
              {ticket?.customer_name || 'Cliente'}
            </div>
            <div style={{ fontSize: '12px', color: '#888780' }}>
              {ticket?.customer_phone || ''}
            </div>
          </div>
        </div>
        {ticket?.status !== 'closed' ? (
          <div className="d-flex" style={{ gap: '6px' }}>
            <button
              type="button"
              onClick={onToggleDetails}
              style={{
                fontSize: '11px',
                padding: '4px 10px',
                borderRadius: '6px',
                border: showDetails
                  ? '0.5px solid #85B7EB'
                  : '0.5px solid #B4B2A9',
                background: showDetails ? '#E6F1FB' : '#fff',
                color: showDetails ? '#0C447C' : '#5F5E5A',
                cursor: 'pointer',
              }}
            >
              <i
                className="las la-info-circle"
                style={{ marginRight: '4px', fontSize: '13px' }}
              />
              Detalhes
            </button>
            <button
              type="button"
              onClick={() => setShowTransferModal(true)}
              style={{
                fontSize: '11px',
                padding: '4px 10px',
                borderRadius: '6px',
                border: '0.5px solid #B4B2A9',
                background: '#fff',
                color: '#5F5E5A',
                cursor: 'pointer',
              }}
            >
              Transferir
            </button>
            <button
              type="button"
              onClick={() => setShowCloseConfirm(true)}
              style={{
                fontSize: '11px',
                padding: '4px 10px',
                borderRadius: '6px',
                border: '0.5px solid #F09595',
                background: '#FCEBEB',
                color: '#A32D2D',
                cursor: 'pointer',
              }}
            >
              Encerrar
            </button>
          </div>
        ) : (
          <div className="d-flex" style={{ gap: '6px' }}>
            <button
              type="button"
              onClick={onToggleDetails}
              style={{
                fontSize: '11px',
                padding: '4px 10px',
                borderRadius: '6px',
                border: showDetails
                  ? '0.5px solid #85B7EB'
                  : '0.5px solid #B4B2A9',
                background: showDetails ? '#E6F1FB' : '#fff',
                color: showDetails ? '#0C447C' : '#5F5E5A',
                cursor: 'pointer',
              }}
            >
              <i
                className="las la-info-circle"
                style={{ marginRight: '4px', fontSize: '13px' }}
              />
              Detalhes
            </button>
            <button
              type="button"
              onClick={() => onReopen(ticket?.id)}
              style={{
                fontSize: '11px',
                padding: '4px 10px',
                borderRadius: '6px',
                border: '0.5px solid #0F6E56',
                background: '#E1F5EE',
                color: '#085041',
                cursor: 'pointer',
              }}
            >
              <i
                className="las la-redo-alt"
                style={{ marginRight: '4px', fontSize: '13px' }}
              />
              Reabrir
            </button>
          </div>
        )}
      </div>

      {/* Barra de contexto */}
      <div
        style={{
          padding: '6px 14px',
          background: '#f8f8f6',
          borderBottom: '0.5px solid #dee2e6',
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          fontSize: '11px',
          flexWrap: 'wrap',
          flexShrink: 0,
        }}
      >
        {ticket?.subscriber_name && (
          <span
            style={{
              background: '#EEEDFE',
              color: '#3C3489',
              padding: '2px 8px',
              borderRadius: '10px',
            }}
          >
            {ticket.subscriber_name}
          </span>
        )}
        {ticket?.store_name && (
          <span
            style={{
              background: '#E1F5EE',
              color: '#085041',
              padding: '2px 8px',
              borderRadius: '10px',
            }}
          >
            {ticket.store_name}
          </span>
        )}
        {ticket?.order_number && (
          <span style={{ color: '#5F5E5A' }}>
            Pedido #{ticket.order_number}
          </span>
        )}
      </div>

      {/* Feed de mensagens — min-height: 0 é essencial para flex + overflow */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {loadingMessages ? (
          <div className="d-flex align-items-center justify-content-center h-100">
            <Loading />
          </div>
        ) : (
          <PerfectScrollbar
            style={{
              height: '100%',
              background: '#ECE5DD',
              padding: '12px',
            }}
            containerRef={el => {
              scrollRef.current = el;
            }}
          >
            {messages.map((message, index) => {
              const msgId =
                message.id || message.ref || `msg-${message.created_at}`;

              // Quem enviou?
              const isFromAgent =
                message.created_by?.id === currentUser?.id ||
                (message.source === 'internal' && !!message.created_by);
              const isFromViddy = message.external_author_name === 'Viddy';

              // Alinhamento: direita = agente ou Viddy, esquerda = cliente
              const alignRight = isFromAgent || isFromViddy;

              // Tipo de mensagem
              const isWhatsappClient =
                message.source === 'whatsapp' && !isFromViddy;
              const isSystem =
                !message.created_by &&
                !isWhatsappClient &&
                !isFromViddy &&
                message.external_author_name === 'Sistema';

              return (
                <React.Fragment key={msgId}>
                  {showDayRow(message, index) && message.created_at && (
                    <Row className="w-100 mx-0 my-3 d-flex flex-row justify-content-center">
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: '#5F5E5A',
                          background: 'rgba(255,255,255,0.7)',
                          padding: '2px 12px',
                          borderRadius: '10px',
                        }}
                      >
                        {format(parseISO(message.created_at), 'dd/MMM/yyyy', {
                          locale: ptBR,
                        })}
                      </span>
                    </Row>
                  )}
                  <MessageBubble
                    message={message}
                    alignRight={alignRight}
                    isFromAgent={isFromAgent}
                    isSystem={isSystem}
                  />
                </React.Fragment>
              );
            })}
          </PerfectScrollbar>
        )}
      </div>

      {/* Input — flexShrink: 0 garante que não colapsa */}
      {/* Input ou somente leitura */}
      {ticket?.status !== 'closed' ? (
        <div
          style={{
            padding: '10px 14px',
            borderTop: '0.5px solid #dee2e6',
            background: '#fff',
            position: 'relative',
            flexShrink: 0,
          }}
        >
          {showQuickReplies && (
            <QuickReplyDropdown
              quickReplies={quickReplies}
              filter={quickReplyFilter}
              onSelect={handleSelectQuickReply}
              onClose={() => setShowQuickReplies(false)}
            />
          )}
          <div className="d-flex" style={{ gap: '8px', alignItems: 'center' }}>
            <div style={{ position: 'relative' }} ref={emojiRef}>
              <button
                type="button"
                onClick={() => setShowEmojiPicker(prev => !prev)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '20px',
                  padding: '2px',
                  lineHeight: 1,
                  color: showEmojiPicker ? '#0C447C' : '#888780',
                }}
              >
                😊
              </button>
              {showEmojiPicker && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '40px',
                    left: 0,
                    zIndex: 20,
                  }}
                >
                  <EmojiPicker
                    onEmojiClick={handleEmojiClick}
                    width={300}
                    height={350}
                    searchPlaceHolder="Buscar emoji..."
                    previewConfig={{ showPreview: false }}
                    skinTonesDisabled
                    lazyLoadEmojis
                  />
                </div>
              )}
            </div>
            {/* Botão anexo */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sendingAttachment}
              style={{
                background: 'none',
                border: 'none',
                cursor: sendingAttachment ? 'wait' : 'pointer',
                fontSize: '18px',
                padding: '2px',
                lineHeight: 1,
                color: sendingAttachment ? '#B4B2A9' : '#888780',
              }}
              title={sendingAttachment ? 'Enviando...' : 'Enviar arquivo'}
            >
              <i
                className={
                  sendingAttachment
                    ? 'las la-spinner la-spin'
                    : 'las la-paperclip'
                }
              />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
            />
            <input
              ref={inputRef}
              type="text"
              className="form-control"
              placeholder="Digite uma mensagem ou / para atalhos..."
              value={currentMessage}
              onChange={handleInputChange}
              onKeyDown={e => {
                if (e.keyCode === 13 && !showQuickReplies) handleSend();
                if (e.keyCode === 27 && showQuickReplies)
                  setShowQuickReplies(false);
              }}
              style={{ fontSize: '13px' }}
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={!currentMessage.trim()}
              style={{
                fontSize: '12px',
                padding: '6px 16px',
                borderRadius: '6px',
                border: '0.5px solid #0F6E56',
                background: currentMessage.trim() ? '#E1F5EE' : '#f2f2f0',
                color: currentMessage.trim() ? '#085041' : '#B4B2A9',
                cursor: currentMessage.trim() ? 'pointer' : 'default',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s',
              }}
            >
              Enviar
            </button>
          </div>
        </div>
      ) : (
        <div
          style={{
            padding: '10px 14px',
            borderTop: '0.5px solid #dee2e6',
            background: '#f8f8f6',
            flexShrink: 0,
            textAlign: 'center',
            fontSize: '12px',
            color: '#888780',
          }}
        >
          Atendimento encerrado — somente leitura
        </div>
      )}

      <TransferModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        onTransfer={userId => {
          onTransfer(ticket?.id, userId);
          setShowTransferModal(false);
        }}
      />

      <ConfirmModal
        isOpen={showCloseConfirm}
        title="Encerrar atendimento"
        message={`Deseja encerrar o atendimento com ${
          ticket?.customer_name || 'este cliente'
        }?`}
        confirmLabel="Encerrar"
        confirmColor="danger"
        onConfirm={() => {
          setShowCloseConfirm(false);
          onClose(ticket?.id);
        }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </div>
  );
}
